import { initializeApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  connectAuthEmulator,
  signInWithCustomToken,
  type Auth,
} from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore, type Firestore } from 'firebase/firestore';

const env = import.meta.env;

// Local E2E harness only: when VITE_USE_FIREBASE_EMULATOR is set, point the
// Auth + Firestore SDKs at the Firebase emulator instead of prod. Default OFF
// (env var absent in every prod/staging build) so this is a strict NO-OP in
// production — the connect* calls and the auto-login below never run there.
export const useEmulator = Boolean(env.VITE_USE_FIREBASE_EMULATOR);

export const isConfigured = Boolean(env.VITE_FIREBASE_API_KEY && env.VITE_FIREBASE_PROJECT_ID);

// localStorage key that holds the agent's selected tenant (shared contract).
// Shape: { code, databaseId, name, logoUrl?, primaryColor? } where
// databaseId === code for these tenants (named Firestore DB id == tenant code).
export const SELECTED_TENANT_KEY = 'chatolam.selectedTenant';

export interface StoredTenant {
  code: string;
  databaseId: string;
  name: string;
  logoUrl?: string;
  primaryColor?: string;
}

function readStoredTenant(): StoredTenant | null {
  try {
    const raw = localStorage.getItem(SELECTED_TENANT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredTenant>;
    if (parsed && typeof parsed.code === 'string' && parsed.code) {
      return {
        code: parsed.code,
        databaseId: parsed.databaseId || parsed.code,
        name: parsed.name || parsed.code,
        logoUrl: parsed.logoUrl,
        primaryColor: parsed.primaryColor,
      };
    }
  } catch {
    // ignore malformed JSON / unavailable storage
  }
  return null;
}

/**
 * Active tenant CODE for header injection (X-Tenant-Code) and elsewhere.
 * Falls back to the build-time VITE_FIRESTORE_DATABASE_ID, then '' (no header).
 */
export function getActiveTenantCode(): string {
  const stored = readStoredTenant();
  if (stored) return stored.code;
  return env.VITE_FIRESTORE_DATABASE_ID || '';
}

// In demo mode (no Firebase env), we still need objects for the auth + db
// exports so dependent modules don't crash at import time. The App component
// shows a "Not configured" page instead of letting these be used.
const app = initializeApp({
  apiKey: env.VITE_FIREBASE_API_KEY || 'demo-key',
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || 'demo.firebaseapp.com',
  projectId: env.VITE_FIREBASE_PROJECT_ID || 'chatolam-demo',
  appId: env.VITE_FIREBASE_APP_ID || 'demo-app-id',
});

const isNative =
  typeof window !== 'undefined' &&
  (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.() === true;

// Firebase Auth v10 awaits navigator.locks.request() to coordinate token
// refresh. Inside the iOS WKWebView (Capacitor) that callback never resolves,
// so onAuthStateChanged can hang and the app is stuck on "Loading…". There are
// no multiple tabs in a native shell, so the lock is unnecessary — drop the API
// on native so Firebase uses its lock-free fallback.
if (isNative) {
  try {
    Object.defineProperty(navigator, 'locks', { configurable: true, value: undefined });
  } catch {
    /* ignore — older WebView without a configurable navigator.locks */
  }
}

// On native (iOS WKWebView) the default IndexedDB persistence can hang during
// init — the IDB open callback never fires, so onAuthStateChanged never resolves
// and the app sticks on "Loading…". Initialize with localStorage persistence
// first there (always synchronously available in WKWebView), falling back to
// IndexedDB. On web keep the standard getAuth().
export const auth: Auth = isNative
  ? initializeAuth(app, { persistence: [browserLocalPersistence, indexedDBLocalPersistence] })
  : getAuth(app);

// Firestore is bound to the active tenant's named database at app start.
// Switching tenants persists the new selection then reloads the page (see
// useAuth.setTenant), so this picks up the new databaseId on the next boot.
// Under the emulator we always bind '(default)' — the emulator has no named DBs.
const databaseId = useEmulator
  ? '(default)'
  : readStoredTenant()?.databaseId || env.VITE_FIRESTORE_DATABASE_ID || '(default)';
export const db: Firestore = initializeFirestore(app, {}, databaseId);

// E2E harness wiring (gated). connectAuthEmulator / connectFirestoreEmulator are
// idempotent-safe to call once right after init, before any auth/db use.
if (useEmulator) {
  const authHost = env.VITE_FIREBASE_AUTH_EMULATOR || 'http://127.0.0.1:9099';
  const fsHost = env.VITE_FIRESTORE_EMULATOR_HOST || '127.0.0.1:8088';
  const [fsHostname, fsPort] = fsHost.split(':');
  try {
    connectAuthEmulator(auth, authHost, { disableWarnings: true });
  } catch {
    /* already connected (HMR) */
  }
  try {
    connectFirestoreEmulator(db, fsHostname, Number(fsPort));
  } catch {
    /* already connected (HMR) */
  }
  // Test-only auto-login: global-setup mints a custom token with the agent's
  // claims (role/tenantCode/inboxIds) and hands it to the build via this env
  // var. Signing in with it gives the app a real authenticated session carrying
  // those claims — no Google popup. Strictly behind the emulator gate.
  const testToken = env.VITE_TEST_CUSTOM_TOKEN;
  if (testToken && typeof window !== 'undefined') {
    void signInWithCustomToken(auth, testToken).catch((e) => {
      // Surface in the console so a Playwright run can see why login failed.
      console.error('[emulator] test auto-login failed', e);
    });
  }
}

/**
 * The named Firestore database id this session actually bound to at import.
 * App compares it against the active tenant; on a mismatch (e.g. a fresh
 * single-tenant login where nothing was persisted yet) it persists the
 * selection and reloads once so Firestore rebinds to the correct named DB.
 */
export const boundDatabaseId = databaseId;

export const apiBase = (env.VITE_API_BASE || '/api').replace(/\/$/, '');
